import { type DBSchema, type IDBPDatabase, openDB } from 'idb'

/**
 * The offline mutation queue.
 *
 * My lab is a basement with no signal, and that is exactly where I need to tick
 * something off. So a mutation made offline is written to IndexedDB and replayed
 * on reconnect — which only works because the API was built for it: every
 * mutating route takes a client-supplied idempotency key and stores its
 * response, so replaying a mutation that already landed is a no-op rather than
 * a duplicate.
 *
 * The key is generated ONCE, when the mutation is queued, and travels with it
 * forever. Generating it at send time would defeat the entire mechanism: each
 * retry would look like a new request.
 */

export type QueuedStatus = 'pending' | 'failed'

export interface QueuedMutation {
  id?: number
  /** Generated at enqueue time and never regenerated. */
  idempotencyKey: string
  method: 'POST' | 'PATCH' | 'PUT' | 'DELETE'
  path: string
  body: unknown
  /** What this was, in words, for the failed-queue UI. */
  description: string
  createdAt: string
  attempts: number
  lastError: string | null
  status: QueuedStatus
}

interface QueueDb extends DBSchema {
  mutations: {
    key: number
    value: QueuedMutation
    indexes: { 'by-created': string; 'by-status': QueuedStatus }
  }
}

let dbPromise: Promise<IDBPDatabase<QueueDb>> | null = null

function db(): Promise<IDBPDatabase<QueueDb>> {
  dbPromise ??= openDB<QueueDb>('fall26-queue', 1, {
    upgrade(database) {
      const store = database.createObjectStore('mutations', { keyPath: 'id', autoIncrement: true })
      store.createIndex('by-created', 'createdAt')
      store.createIndex('by-status', 'status')
    },
  })
  return dbPromise
}

export function newIdempotencyKey(): string {
  return crypto.randomUUID()
}

/** Client-generated item id, so an offline create can be referred to at once. */
export function newClientId(): string {
  return `c-${crypto.randomUUID()}`
}

export async function enqueue(
  mutation: Omit<QueuedMutation, 'id' | 'createdAt' | 'attempts' | 'lastError' | 'status'>,
): Promise<number> {
  const database = await db()
  return database.add('mutations', {
    ...mutation,
    createdAt: new Date().toISOString(),
    attempts: 0,
    lastError: null,
    status: 'pending',
  })
}

/**
 * FIFO. Order is load-bearing: a create followed by an edit of the same item
 * must replay in that order, or the edit arrives for a row that does not exist.
 */
export async function listQueued(): Promise<QueuedMutation[]> {
  const database = await db()
  const all = await database.getAllFromIndex('mutations', 'by-created')
  return all
}

export async function pendingCount(): Promise<number> {
  return (await listQueued()).filter((m) => m.status === 'pending').length
}

export async function failedCount(): Promise<number> {
  return (await listQueued()).filter((m) => m.status === 'failed').length
}

export async function remove(id: number): Promise<void> {
  const database = await db()
  await database.delete('mutations', id)
}

export async function markFailed(id: number, error: string): Promise<void> {
  const database = await db()
  const existing = await database.get('mutations', id)
  if (!existing) return
  await database.put('mutations', { ...existing, status: 'failed', lastError: error, attempts: existing.attempts + 1 })
}

export async function markAttempted(id: number, error: string): Promise<void> {
  const database = await db()
  const existing = await database.get('mutations', id)
  if (!existing) return
  await database.put('mutations', { ...existing, attempts: existing.attempts + 1, lastError: error })
}

/** Put a failed mutation back in the queue, e.g. after signing in again. */
export async function retryFailed(): Promise<number> {
  const database = await db()
  const all = await database.getAll('mutations')
  const failed = all.filter((m) => m.status === 'failed')
  for (const mutation of failed) {
    await database.put('mutations', { ...mutation, status: 'pending', lastError: null })
  }
  return failed.length
}

export async function discard(id: number): Promise<void> {
  await remove(id)
}

/** Test seam. */
export async function clearQueue(): Promise<void> {
  const database = await db()
  await database.clear('mutations')
}
